import { useCallback, useEffect, useRef, useState } from 'react';

import { getAnalytics, logEvent } from 'firebase/analytics';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { setMessage } from '../../toasts/toastSlice';
import { setOrdersModalConfig, setOrdersToDeliverFilters } from '../orderSlice';
import {
  dataUrlToBlob,
  handleAxiosErrorMessage,
  normalizeName,
} from '../../../utils';
import { NOTIFICATION_TYPES } from '../../../config/consts/commons';
import {
  VALID_RECIPIENT_NAME_REGEX_PATTERN,
  VALID_RECIPIENT_DOCUMENT_REGEX_PATTERN,
} from '../../../config/consts/orders';

import useUploadDeliveredOrderPhoto from './useUploadDeliveredOrderPhoto';
import useConfirmDeliveredToConsumer from './useConfirmDeliveredToConsumer';
import useConfirmPickedUpAndDelivered from './useConfirmPickedUpAndDelivered';
import useUploadOrderToReturnPhoto from './useUploadOrderToReturnPhoto';
import useUploadOrderSignature from './useUploadOrderSignature';
import useEcommerceFields from './useEcommerceFields';
import useDeliveryGeolocation from './useDeliveryGeolocation';
import { RAPPI_ECOMMERCE_KEY } from '../../../config/ecommerceFieldsConfig';
import {
  generateDeliveryReceiptImage,
  buildDeliveryReceiptData,
} from '../utils';
import { getDeliveryReceiptTemplate } from '../utils/deliveryReceiptTemplate';

export default function useOrdersToDeliverModal() {
  const { t, i18n } = useTranslation([
    'system',
    'description',
    'button',
    'form',
    'validation',
  ]);
  const { ordersToDeliverFilters, ordersModalConfig, confirmationAnalytics } =
    useSelector((state) => state.orders);
  const analytics = getAnalytics();
  const { order } = ordersModalConfig;

  const { config, validateRequiredFields } = useEcommerceFields(
    order?.ecommerce_name || 'default'
  );

  const {
    location,
    setLocation,
    customLocation,
    setCustomLocation,
    locationStatus,
    setLocationStatus,
    attempts,
    setAttempts,
    errorMessage,
    setErrorMessage,
    validateLocation,
  } = useDeliveryGeolocation();

  const [buttonLabel, setButtonLabel] = useState('Confirmar');

  const [comment, setComment] = useState('');
  const [commentPickedUpOrder, setCommentPickedUpOrder] = useState('');
  const [recipient, setRecipient] = useState('');
  const [recipientDocument, setRecipientDocument] = useState('');
  const [recipientRelationship, setRecipientRelationship] = useState('');
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);

  const [rawSignatureDataUrl, setRawSignatureDataUrl] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const [wasPickedUp, setWasPickedUp] = useState(false);

  const isSubmittingRef = useRef(false);

  const uploadedPhotoUrlRef = useRef(null);
  const uploadedPickupPhotoUrlRef = useRef(null);
  const uploadedReceiptUrlRef = useRef(null);

  const uploadDeliveredOrderPhotoMutation = useUploadDeliveredOrderPhoto();
  const confirmDeliveredToConsumerMutation = useConfirmDeliveredToConsumer();
  const confirmPickedUpAndDeliveredMutation = useConfirmPickedUpAndDelivered();
  const uploadOrderToReturnPhotoMutation = useUploadOrderToReturnPhoto();
  const uploadOrderSignatureMutation = useUploadOrderSignature();

  const dispatch = useDispatch();

  useEffect(() => {
    getDeliveryReceiptTemplate(t, i18n.language).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const computeRules = useCallback(
    ({ file, filePickedUpOrder, isFileCompressing }) => {
      const shouldValidateRecipientFields = !order.related_order || wasPickedUp;

      const validateRecipientFields = () => {
        const cleanName = normalizeName(recipient);
        let isRecipientNameInvalid = false;
        let isRecipientDocumentInvalid = false;

        if (recipient) {
          isRecipientNameInvalid =
            !VALID_RECIPIENT_NAME_REGEX_PATTERN.test(cleanName);
        }

        if (recipientDocument) {
          isRecipientDocumentInvalid =
            !VALID_RECIPIENT_DOCUMENT_REGEX_PATTERN.test(recipientDocument);
        }

        if (!shouldValidateRecipientFields) {
          return isRecipientNameInvalid || isRecipientDocumentInvalid;
        }
        return (
          isRecipientNameInvalid ||
          isRecipientDocumentInvalid ||
          validateRequiredFields({
            recipientDocument,
            recipientRelationship,
            signature: rawSignatureDataUrl,
          })
        );
      };

      if (order.related_order) {
        return (
          !filePickedUpOrder ||
          (wasPickedUp &&
            (!file ||
              !recipient ||
              validateLocation() ||
              validateRecipientFields()))
        );
      }

      return (
        !recipient ||
        !file ||
        isFileCompressing ||
        validateLocation() ||
        validateRecipientFields()
      );
    },
    [
      order,
      recipient,
      recipientDocument,
      recipientRelationship,
      rawSignatureDataUrl,
      wasPickedUp,
      validateLocation,
      validateRequiredFields,
    ]
  );

  const resetModal = useCallback(() => {
    const { show, ...rest } = ordersModalConfig;
    setComment('');
    setCommentPickedUpOrder('');
    setRecipient('');
    setRecipientDocument('');
    setRecipientRelationship('');
    setRawSignatureDataUrl(null);
    setSignatureModalOpen(false);
    setButtonLabel(t('confirm', { ns: 'button' }));
    setIsSubmitting(false);
    setShowSubmitError(false);
    isSubmittingRef.current = false;
    uploadedPhotoUrlRef.current = null;
    uploadedPickupPhotoUrlRef.current = null;
    uploadedReceiptUrlRef.current = null;
    dispatch(setOrdersModalConfig({ ...rest, show: false }));
  }, [ordersModalConfig, t, dispatch]);

  const changeModal = useCallback(() => {
    dispatch(
      setOrdersModalConfig({
        titleOpening: t('orders.deliver.modal.wish_pickup_order', {
          ns: 'description',
        }),
        show: true,
        order,
        modalType: 'toDeliver',
      })
    );
  }, [dispatch, t, order]);

  const uploadSignatureFile = async ({ id_order }) => {
    const isRappi =
      order?.ecommerce_name?.toLowerCase().includes(RAPPI_ECOMMERCE_KEY) ??
      false;
    const useReceiptTemplate = config.fields?.includes('signature') && !isRappi;

    let sigFile;
    if (useReceiptTemplate) {
      const receiptData = buildDeliveryReceiptData({
        order,
        recipient,
        recipientDocument,
        recipientRelationship,
        signatureDataUrl: rawSignatureDataUrl,
        signatureDate: new Date().toISOString(),
      });
      const receiptBlob = await generateDeliveryReceiptImage(receiptData, {
        t,
        localeKey: i18n.language,
      });
      sigFile = new File([receiptBlob], 'comprovante-entrega.jpeg', {
        type: 'image/jpeg',
      });
    } else {
      const blob = dataUrlToBlob(rawSignatureDataUrl);
      sigFile = new File([blob], 'signature.jpeg', { type: 'image/jpeg' });
    }

    const sigFormData = new FormData();
    sigFormData.append('file', sigFile);

    if (!uploadedReceiptUrlRef.current) {
      const response = await uploadOrderSignatureMutation.mutateAsync({
        id_order,
        formData: sigFormData,
      });
      uploadedReceiptUrlRef.current = response.data;
    }

    return uploadedReceiptUrlRef.current;
  };

  const handleSubmit = async ({ file, filePickedUpOrder }) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const { id_order, order_code, related_order } = order;

    if (!order.related_order) {
      setShowSubmitError(false);
      const formData = new FormData();
      formData.append('file', file);
      setIsSubmitting(true);
      try {
        if (!uploadedPhotoUrlRef.current) {
          const uploadResponse =
            await uploadDeliveredOrderPhotoMutation.mutateAsync({
              id_order,
              formData,
            });
          uploadedPhotoUrlRef.current = uploadResponse.data;
        }
        const photoUrl = uploadedPhotoUrlRef.current;

        const photos = [{ photo_link: photoUrl, photo_type: 'order' }];

        if (rawSignatureDataUrl) {
          const signatureUrl = await uploadSignatureFile({ id_order });
          photos.push({ photo_link: signatureUrl, photo_type: 'signature' });
        }

        const cleanRecipientName = normalizeName(recipient);

        const payload = {
          id_order,
          photos,
          receptor: { name: cleanRecipientName },
          comment,
        };

        if (recipientDocument) {
          payload.receptor.identity_number = recipientDocument;
        }

        if (recipientRelationship) {
          payload.receptor.relationship = recipientRelationship;
        }

        if (location.latitude && location.longitude) {
          payload.latitude = parseFloat(location.latitude);
          payload.longitude = parseFloat(location.longitude);
        } else {
          payload.address_location = customLocation;
        }

        await confirmDeliveredToConsumerMutation.mutateAsync(payload);

        isSubmittingRef.current = false;
        resetModal();
        dispatch(setOrdersToDeliverFilters({ ...ordersToDeliverFilters }));
        dispatch(
          setMessage({
            body: t('ORDER_DELIVER_SUCCESS', {
              ns: 'system',
              order: order_code,
            }),
            type: NOTIFICATION_TYPES.SUCCESS,
          })
        );
        logEvent(analytics, `${confirmationAnalytics}`);
      } catch (err) {
        const message = handleAxiosErrorMessage(err);
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        setButtonLabel(t('confirm_return', { ns: 'button' }));
        setShowSubmitError(true);
        dispatch(
          setMessage({
            body: t(message, { ns: 'system' }),
            type: NOTIFICATION_TYPES.ERROR,
          })
        );
      }
    } else {
      if (!wasPickedUp && filePickedUpOrder) {
        isSubmittingRef.current = false;
        setWasPickedUp(true);
        changeModal();
      } else {
        setShowSubmitError(false);

        const formDataPickedUpOrder = new FormData();
        formDataPickedUpOrder.append('file', filePickedUpOrder);

        const formData = new FormData();
        formData.append('file', file);

        setIsSubmitting(true);
        try {
          if (!uploadedPickupPhotoUrlRef.current) {
            const uploadResponsePickedUpOrder =
              await uploadOrderToReturnPhotoMutation.mutateAsync({
                id_order: related_order.id_order,
                formData: formDataPickedUpOrder,
              });
            uploadedPickupPhotoUrlRef.current =
              uploadResponsePickedUpOrder.data;
          }
          const pickupPhotoUrl = uploadedPickupPhotoUrlRef.current;

          const pickedUp = {
            id_order: related_order.id_order,
            comment: commentPickedUpOrder,
            photo_link: pickupPhotoUrl,
          };

          if (!uploadedPhotoUrlRef.current) {
            const uploadResponse =
              await uploadDeliveredOrderPhotoMutation.mutateAsync({
                id_order,
                formData,
              });
            uploadedPhotoUrlRef.current = uploadResponse.data;
          }
          const photoUrl = uploadedPhotoUrlRef.current;

          const photosDelivered = [
            { photo_link: photoUrl, photo_type: 'order' },
          ];

          if (rawSignatureDataUrl) {
            const signatureUrl = await uploadSignatureFile({ id_order });
            photosDelivered.push({
              photo_link: signatureUrl,
              photo_type: 'signature',
            });
          }

          const cleanRecipientName = normalizeName(recipient);

          const delivered = {
            id_order,
            photos: photosDelivered,
            photo_link: photoUrl,
            receptor_name: cleanRecipientName,
            receptor: { name: cleanRecipientName },
            comment,
          };

          if (recipientDocument) {
            delivered.receptor.identity_number = recipientDocument;
          }

          if (recipientRelationship) {
            delivered.receptor.relationship = recipientRelationship;
          }

          if (location.latitude && location.longitude) {
            pickedUp.latitude = location.latitude.toString();
            pickedUp.longitude = location.longitude.toString();
            delivered.latitude = parseFloat(location.latitude);
            delivered.longitude = parseFloat(location.longitude);
          } else {
            pickedUp.address_location = customLocation;
            delivered.address_location = customLocation;
          }

          await confirmPickedUpAndDeliveredMutation.mutateAsync({
            pickedUp,
            delivered,
          });

          isSubmittingRef.current = false;
          resetModal();
          dispatch(setOrdersToDeliverFilters({ ...ordersToDeliverFilters }));
          dispatch(
            setMessage({
              body: t('ORDER_WITHDRAW_SUCCESS', {
                ns: 'system',
                order: related_order.order_code,
              }),
              type: NOTIFICATION_TYPES.SUCCESS,
            })
          );
          dispatch(
            setMessage({
              body: t('ORDER_DELIVER_SUCCESS', {
                ns: 'system',
                order: order_code,
              }),
              type: NOTIFICATION_TYPES.SUCCESS,
            })
          );
        } catch (err) {
          const message = handleAxiosErrorMessage(err);
          isSubmittingRef.current = false;
          setIsSubmitting(false);
          setButtonLabel(t('confirm_return', { ns: 'button' }));
          setShowSubmitError(true);
          dispatch(
            setMessage({
              body: t(message, { ns: 'system' }),
              type: NOTIFICATION_TYPES.ERROR,
            })
          );
        }
      }
    }
  };

  const handleSignatureConfirm = (signature) => {
    setRawSignatureDataUrl(signature);
    setSignatureModalOpen(false);
  };

  return {
    t,
    order,
    config,
    computeRules,
    location,
    setLocation,
    customLocation,
    setCustomLocation,
    locationStatus,
    setLocationStatus,
    attempts,
    setAttempts,
    errorMessage,
    setErrorMessage,
    buttonLabel,
    comment,
    setComment,
    commentPickedUpOrder,
    setCommentPickedUpOrder,
    recipient,
    setRecipient,
    recipientDocument,
    setRecipientDocument,
    recipientRelationship,
    setRecipientRelationship,
    signatureModalOpen,
    setSignatureModalOpen,
    signatureData: rawSignatureDataUrl,
    isSubmitting,
    showSubmitError,
    wasPickedUp,
    handleSubmit,
    handleSignatureConfirm,
    resetModal,
  };
}
